import { useState, useEffect, useCallback, ChangeEvent, useRef } from "react";
import styled from "styled-components";
import {
  Paper,
  Grid,
  Box,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Checkbox,
  Toolbar,
} from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";

import {
  Tooltip,
  ButtonSubmit,
  ButtonCancel,
  SearchInput,
  Select,
  ButtonAdd,
  Loading,
  DialogYesNo,
} from "components/shared";
import { Icons } from "components/Icons";
import { RowNoData } from "components";
import { useTable, useRouter, useModulockerSet } from "hooks";
import { IModulockerSet } from "config/interfaces";
import { TableTitle } from "./styles";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    toolbar: {
      paddingBottom: theme.spacing(2),
      borderRadius: "4px",
      background: "var(--backgroundContent)",
      width: "100%",
      "& .MuiToolbar-regular": {
        padding: 0,
      },
    },
    table: {
      borderRadius: "4px",
      background: "var(--backgroundContent)",
      width: "100%",
    },
    margin: {
      margin: theme.spacing(1),
    },
    withoutLabel: {
      marginTop: theme.spacing(3),
    },
    textField: {
      maxWidth: "calc(100% - 12px)",
    },
    input: {
      "& .MuiInputBase-input": {
        boxShadow: "none",
        fontSize: "1.2rem",
        fontWeight: "500",
        textTransform: "uppercase",
      },
    },
    checkbox: {
      "&.MuiCheckbox-colorSecondary.Mui-checked": {
        color: "var(--orange)",
      },
      "& .MuiSvgIcon-root": {
        width: "1.4rem",
      },
    },
  })
);

interface TableProps {
  title?: string;
}

const filters = [
  {
    label: "STATUS",
    value: "status",
  },
];

interface HeadCell {
  disableSorting?: boolean;
  id: keyof IModulockerSet.Table;
  label: string;
}

// TABLE HEAD
const headCells: HeadCell[] = [
  { id: "description", label: "MODELO" },
  { id: "sku", label: "SKU" },
  { id: "modulockerTemplateQuantity", label: "ARMÁRIOS" },
  { id: "totalBoxes", label: "BOXES" },
  { id: "status", label: "STATUS" },
];

const SelectedItems = styled.span`
  text-transform: uppercase;
  font-weight: 700;
  font-size: 1.2rem;
  width: fit-content;
`;

interface DataState {
  filter: string | null;
  status?: string;
}

const initialState: DataState = {
  filter: null,
  status: "0",
};

export default function TableModulockerSet({ title }: TableProps) {
  const [data, setData] = useState(initialState);
  const [rows, setRows] = useState([] as IModulockerSet.ModulockerSet[]);

  const [openInactivate, setOpenInactivate] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openActivate, setOpenActivate] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedRows, setSelectedRows] = useState([] as Array<string>);
  const [selectedModulockerSet, setSelectedModulockerSet] = useState(
    {} as IModulockerSet.ModulockerSet
  );
  const [openDetails, setOpenDetails] = useState(false);
  const router = useRouter();

  const [filterFn, setFilterFn] = useState({
    fn: (items: any) => {
      return items;
    },
  });
  const {
    loading,
    sets: records,
    getModulockerSetsByFilters,
    getAllModulockerSets,
    deleteModulockerSet,
  } = useModulockerSet();

  const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } =
    useTable({
      records,
      headCells,
      filterFn,
      config: { checkbox: true, actions: true },
    });

  let inputSearchRef = useRef<HTMLInputElement | null>(null);

  const classes = useStyles();

  useEffect(() => {
    setRows(records);
  }, [records]);

  const onSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>, newId: string) => {
      if (event.target.checked) {
        let newSelectedRows = [...selectedRows];
        newSelectedRows.push(newId);
        setSelectedRows(newSelectedRows);
      } else {
        const newSelectedRows = selectedRows.filter((id) => id !== newId);
        setSelectedRows(newSelectedRows);
      }
    },
    [selectedRows]
  );

  const onSelectAll = useCallback((selectedRows: Array<string>) => {
    setSelectedRows(selectedRows);
  }, []);

  const onClick = useCallback(
    (modulockerSet: IModulockerSet.ModulockerSet, open: boolean) => {
      setSelectedModulockerSet(modulockerSet);
      setOpenDetails(open);
    },
    []
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    let target = e.target;
    setSearch(target.value);
    setFilterFn({
      fn: (items: any) => {
        if (target.value === "") return items;
        else
          return items.filter((modulockerSet: IModulockerSet.ModulockerSet) =>
            modulockerSet.description
              .toLowerCase()
              .includes(target.value.toLowerCase())
          );
      },
    });
  };

  const handleSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    rowId: string
  ) => {
    onSelect(event, rowId);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = rows.map(
        (selectedModulockerTemplate) => selectedModulockerTemplate.uuid ?? ""
      );
      return onSelectAll(newSelecteds);
    }
    onSelectAll([] as Array<string>);
  };

  const isSelected = (transactionId: string) =>
    selectedRows.indexOf(transactionId) !== -1;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;

    setData({ ...data, [name]: value ? value : checked });
  };

  const handleFilterClick = () => {
    switch (data.filter) {
      case "status":
        return getModulockerSetsByFilters();
      default:
        return;
    }
  };

  const handleCancelFilter = () => {
    setData({ ...data, filter: null });
    setSearch("");
    getAllModulockerSets();
  };

  const statusOptions = [
    {
      label: "ATIVO",
      value: "Ativo",
    },
    {
      label: "INATIVO",
      value: "Inativo",
    },
  ];

  const selectedItems =
    selectedRows.length > 0 ? String(selectedRows.length).padStart(2, "0") : 0;

  const deleteMessageText = (
    <>
      <p>
        Confirma a exclusão{" "}
        {selectedRows.length > 1 ? <> dos SETs? </> : <> do SET? </>}
      </p>
    </>
  );

  return (
    <>
      <Paper className={classes.toolbar} elevation={0}>
        <Toolbar>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={6} md={5}>
              <SearchInput
                ref={inputSearchRef}
                value={search}
                onChange={handleSearch}
                placeholder="POR DESCRIÇÃO"
              />
            </Grid>

            <Grid item xs={6} md={6} alignItems="flex-end">
              <Box display="flex" justifyContent="flex-end">
                <Select
                  label="FILTRO"
                  name="filter"
                  choices={filters}
                  value={data.filter}
                  onChange={handleChange}
                />
              </Box>
            </Grid>
          </Grid>
        </Toolbar>

        {data.filter && (
          <Toolbar id="toolbar-period" style={{ marginTop: "16px" }}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item xs={12} md={6}>
                <Paper style={{ padding: "16px" }}>
                  {data.filter === "status" && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <Select
                          label="STATUS"
                          name="status"
                          choices={statusOptions}
                          value={data.status}
                          onChange={handleChange}
                        />
                      </Grid>
                    </Grid>
                  )}

                  <Box mt={3} display="flex" justifyContent="flex-end">
                    <ButtonCancel
                      onClick={handleCancelFilter}
                      style={{ marginRight: "16px" }}
                    >
                      CANCELAR
                    </ButtonCancel>
                    <ButtonSubmit autoFocus onClick={handleFilterClick}>
                      FILTRAR
                    </ButtonSubmit>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Toolbar>
        )}
      </Paper>

      {!loading ? (
        <Paper className={classes.table} elevation={0}>
          {title && <TableTitle>{title}</TableTitle>}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <span>
              <SelectedItems>SELECIONADOS: {selectedItems}</SelectedItems>
              {selectedItems ? (
                <ul style={{ display: "inline", marginLeft: "15px" }}>
                  <Tooltip title="INATIVAR">
                    <IconButton onClick={() => setOpenInactivate(true)}>
                      {" "}
                      <Icons.Disable style={{ fontSize: "18px" }} />{" "}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="EXCLUIR">
                    <IconButton onClick={() => setOpenDeleteDialog(true)}>
                      {" "}
                      <Icons.Delete style={{ fontSize: "18px" }} />{" "}
                    </IconButton>
                  </Tooltip>

                  {selectedRows.length === 1 && (
                    <Tooltip title="EDITAR">
                      <IconButton
                        onClick={() =>
                          router.push(
                            `/configuracoes/modelo/${selectedRows[0]}/editar`
                          )
                        }
                      >
                        <Icons.Edit style={{ fontSize: "18px" }} />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title="REATIVAR">
                    <IconButton onClick={() => setOpenActivate(true)}>
                      {" "}
                      <Icons.Activate style={{ fontSize: "18px" }} />{" "}
                    </IconButton>
                  </Tooltip>
                </ul>
              ) : null}
            </span>
            <ButtonAdd onClick={() => router.push("/configuracoes/sets/novo")}>
              NOVO SET
            </ButtonAdd>
          </Box>

          <TblContainer>
            <TblHead
              numSelected={selectedRows.length}
              rowCount={rows.length}
              onSelectAllClick={handleSelectAllClick}
            />
            <TableBody>
              {recordsAfterPagingAndSorting().length === 0 ? (
                <RowNoData />
              ) : (
                recordsAfterPagingAndSorting().map(
                  (row: IModulockerSet.ModulockerSet, index: any) => {
                    const isItemSelected = isSelected(String(row.uuid));
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        key={row.uuid}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        selected={isItemSelected}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            className={classes.checkbox}
                            checked={isItemSelected}
                            inputProps={{ "aria-labelledby": labelId }}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => handleSelect(event, String(row.uuid))}
                          />
                        </TableCell>
                        <TableCell align="left">{row.description}</TableCell>
                        <TableCell align="left">{row.sku}</TableCell>
                        <TableCell align="left">{row.modQty}</TableCell>
                        <TableCell align="left">{row.totalBoxes}</TableCell>
                        <TableCell align="left">{row.status}</TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => onClick(row, true)}>
                            <Tooltip title="INFORMAÇÕES">
                              <Icons.Info fontSize="large" />
                            </Tooltip>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  }
                )
              )}
            </TableBody>
          </TblContainer>

          <TblPagination />
        </Paper>
      ) : (
        <Box mt={15} mb={15}>
          <Loading />
        </Box>
      )}

      <DialogYesNo
        title="Excluir"
        text={deleteMessageText}
        onClose={() => setOpenDeleteDialog(false)}
        onClick={() => {
          deleteModulockerSet(selectedRows);
          setSelectedRows([]);
          setOpenDeleteDialog(false);
        }}
        open={openDeleteDialog}
      />
    </>
  );
}
